import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  Typography,
  Button,
  TextField,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  IconButton,
  Paper,
  List,
  ListItemButton,
  ListItemText,
  ListItemAvatar,
  Avatar,
  Divider,
  CircularProgress,
  Alert,
  Chip,
  Tabs,
  Tab,
} from '@mui/material';
import Draggable from 'react-draggable';
import {
  Close as CloseIcon,
  EventBusy as CancelIcon,
  Search as SearchIcon,
  Person as PersonIcon,
  CalendarToday as CalendarIcon,
  Add as AddIcon,
  Remove as RemoveIcon,
} from '@mui/icons-material';
import { canCancelAppointment } from './appointmentActions';
import { findServiceByName } from './serviceNameMatch';
import { Appointment } from '../../types';
import { format } from 'date-fns';
import { specialistsApi, servicesApi, schedulingApi, serviceMappingApi } from '../../services/api';
import { formatClock, endTimeLabel, editorDuration, durationChoices, parseTypedTime } from './appointmentClock';
import { providerForService, defaultNewAppointmentPairing } from './providerForService';

interface InitialPatient {
  name: string;
  phone: string;
  email?: string;
  internalId?: string;
  spruceContactId?: string;
}

// Optional pre-fill applied when opening the modal for a brand-new appointment
// (e.g. from an AI scheduling suggestion). Only set when relevant.
interface InitialDraft {
  date?: string; // yyyy-MM-dd
  presentingComplaints?: string;
  // True when this appointment is being created from an AI scheduling
  // suggestion, so we can persist provenance for the History "AI-assisted" badge.
  aiAssisted?: boolean;
}

interface AppointmentModalProps {
  open: boolean;
  appointment: Appointment | null;
  onClose: () => void;
  onSave: (appointment: Appointment) => void;
  /** Cancel the appointment being edited. Omitted while creating one. */
  onCancelAppointment?: (appointment: Appointment) => void;
  initialPatient?: InitialPatient;
  initialDraft?: InitialDraft;
  /**
   * Seed a NEW appointment from an existing one (the row's Duplicate action).
   * Distinct from `appointment`, which means "edit this one" — here the fields
   * are copied but the save still creates.
   */
  prefillFrom?: Appointment | null;
  draggable?: boolean;
}

// Draggable paper component for the dialog
function PaperComponent(props: any) {
  const nodeRef = useRef<HTMLDivElement>(null);
  return (
    <Draggable
      handle="#draggable-dialog-title"
      cancel={'[class*="MuiDialogContent-root"]'}
      nodeRef={nodeRef}
    >
      <Paper ref={nodeRef} {...props} />
    </Draggable>
  );
}

interface Service {
  id: string;
  name: string;
  duration: number;
}

interface Specialist {
  id: string;
  name: string;
}

interface Patient {
  id: string;
  name: string;
  phone: string;
  email?: string;
  internalId?: string;
  spruceContactId?: string;
}




const AppointmentModal: React.FC<AppointmentModalProps> = ({
  open,
  appointment,
  onClose,
  onSave,
  onCancelAppointment,
  initialPatient,
  initialDraft,
  prefillFrom,
  draggable = false,
}) => {
  const isEdit = !!appointment;
  
  // API data
  const [services, setServices] = useState<Service[]>([]);
  const [specialists, setSpecialists] = useState<Specialist[]>([]);
  // Provider/service pairings. Each may carry a duration override in minutes;
  // null means the pairing just uses the service's own duration.
  const [mappings, setMappings] = useState<Array<{ serviceId: string; specialistId: string; duration: number | null }>>([]);
  // Once the user sets the duration by hand we stop re-deriving it underneath them.
  const durationTouched = useRef(false);
  const timeFieldFocused = useRef(false);
  const timeDraftRef = useRef('');
  const timeValueRef = useRef('09:00');
  const [timeDraft, setTimeDraft] = useState('');
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isCreating, setIsCreating] = useState(false);
  
  // Patient search
  const [searchMode, setSearchMode] = useState<'name' | 'phone'>('name');
  const [searchQuery, setSearchQuery] = useState('');
  const [patientResults, setPatientResults] = useState<Patient[]>([]);
  const [isSearching, setIsSearching] = useState(false);
  const [selectedPatient, setSelectedPatient] = useState<Patient | null>(null);

  const [formData, setFormData] = useState({
    patientName: '',
    patientPhone: '',
    patientEmail: '',
    patientDOB: '',
    patientGender: '',
    patientId: '', // Spruce contact ID
    internalPatientId: '', // Internal database ID (e.g., LMD-00001)
    appointmentType: '',
    serviceId: '',
    date: format(new Date(), 'yyyy-MM-dd'),
    time: '09:00',
    duration: 30,
    provider: '',
    specialistId: '',
    room: '',
    presentingComplaints: '',
    clinicalNotes: '',
  });

  timeValueRef.current = formData.time;
  useEffect(() => {
    if (timeFieldFocused.current) return;
    const shown = formatClock(formData.time);
    timeDraftRef.current = shown;
    setTimeDraft(shown);
  }, [formData.time, open]);

  // Fetch services and specialists on mount
  useEffect(() => {
    const fetchData = async () => {
      if (!open) return;
      
      setIsLoading(true);
      try {
        const [servicesRes, specialistsRes, mappingsRes] = await Promise.all([
          servicesApi.getAll(),
          specialistsApi.getActive(),
          serviceMappingApi.getAll(),
        ]);
        
        // API returns arrays directly, not wrapped in { services: [...] }
        const serviceData = servicesRes.data?.services || servicesRes.data || [];
        const servicesArray = Array.isArray(serviceData) ? serviceData : [];
        
        const specialistData = specialistsRes.data?.specialists || specialistsRes.data || [];
        const specialistsArray = Array.isArray(specialistData) ? specialistData : [];
        
        const mappingData = mappingsRes.data?.mappings || mappingsRes.data || [];
        const mappingsArray = Array.isArray(mappingData) ? mappingData : [];

        setServices(servicesArray);
        setSpecialists(specialistsArray);
        setMappings(mappingsArray);
        
      } catch (err) {
        console.error('Failed to fetch data:', err);
        setError('Failed to load services and providers');
      } finally {
        setIsLoading(false);
      }
    };
    
    fetchData();
  }, [open]);

  // The defaults for a NEW appointment are applied below, after the reset
  // effect: two effects here used to set the type and the provider, and the
  // reset that runs in the same commit wiped both, so the dialog opened with
  // nothing chosen and 30 minutes.

  // Search patients
  useEffect(() => {
    const searchPatients = async () => {
      if (searchQuery.length < 2) {
        setPatientResults([]);
        return;
      }
      
      setIsSearching(true);
      try {
        const response = await schedulingApi.searchPatients(searchQuery);
        setPatientResults(response.data?.data || []);
      } catch (err) {
        console.error('Patient search failed:', err);
      } finally {
        setIsSearching(false);
      }
    };
    
    const debounce = setTimeout(searchPatients, 300);
    return () => clearTimeout(debounce);
  }, [searchQuery]);

  // Initialize form with appointment data or reset
  useEffect(() => {
    if (!appointment && prefillFrom) {
      // Imported rows carry appointmentType as a display string ("Follow-Up
      // Visit") with no serviceId, so a straight copy left the service unset
      // and the default-selection effect picked a different one — a 30-minute
      // follow-up duplicated itself as a 90-minute consultation. Match the
      // service by name when there is no id, and treat the copied duration as
      // deliberate so nothing re-derives it underneath.
      const sourceServiceId =
        (prefillFrom as any).serviceId ||
        findServiceByName(
          services,
          prefillFrom.appointmentType || (prefillFrom as any).serviceType,
        )?.id ||
        '';
      durationTouched.current = true;
      setFormData((prev) => ({
        ...prev,
        patientName: prefillFrom.patientName || '',
        patientPhone: prefillFrom.patientPhone || '',
        patientEmail: prefillFrom.patientEmail || '',
        patientDOB: (prefillFrom as any).patientDOB || '',
        patientGender: (prefillFrom as any).patientGender || '',
        patientId: (prefillFrom as any).patientId || '',
        internalPatientId: (prefillFrom as any).internalPatientId || '',
        appointmentType: prefillFrom.appointmentType || (prefillFrom as any).serviceType || '',
        serviceId: sourceServiceId,
        duration: editorDuration(prefillFrom),
        provider: (prefillFrom as any).provider || prefillFrom.specialist || '',
        specialistId: prefillFrom.specialistId || '',
        room: (prefillFrom as any).room || '',
        presentingComplaints: prefillFrom.presentingComplaints || '',
        clinicalNotes: '',
        // Deliberately not copied: the original holds this slot, so reusing it
        // would collide. The person picks when.
        date: initialDraft?.date || format(new Date(), 'yyyy-MM-dd'),
        time: '09:00',
      }));
      return;
    }
    if (appointment) {
      // These two used to be hard-coded empty, so opening ANY appointment to
      // edit showed a blank Appointment Type and a blank Provider — both
      // required — even when the appointment plainly had them. Saving from that
      // state wrote the blanks back. Resolve them from the record: by id where
      // there is one, otherwise by matching the stored name.
      const editServiceId =
        (appointment as any).serviceId ||
        findServiceByName(
          services,
          appointment.appointmentType || (appointment as any).serviceType,
        )?.id ||
        '';
      const editSpecialistId =
        appointment.specialistId ||
        specialists.find(
          (sp) =>
            sp.name.toLowerCase() ===
            String((appointment as any).provider || appointment.specialist || '').toLowerCase(),
        )?.id ||
        '';
      setFormData({
        patientName: appointment.patientName || '',
        patientPhone: appointment.patientPhone || '',
        patientEmail: appointment.patientEmail || '',
        patientDOB: appointment.patientDOB || '',
        patientGender: appointment.patientGender || '',
        patientId: appointment.patientId || '',
        internalPatientId: (appointment as any).internalPatientId || '',
        appointmentType: appointment.appointmentType || (appointment as any).serviceType || '',
        serviceId: editServiceId,
        date: format(new Date(appointment.startTime), 'yyyy-MM-dd'),
        time: format(new Date(appointment.startTime), 'HH:mm'),
        // Its real length, even when none is stored (appointmentClock).
        duration: editorDuration(appointment),
        provider: (appointment as any).provider || appointment.specialist || '',
        specialistId: editSpecialistId,
        room: appointment.room || '',
        presentingComplaints: appointment.presentingComplaints || '',
        clinicalNotes: appointment.clinicalNotes || '',
      });
      setSelectedPatient({
        id: appointment.patientId || '',
        name: appointment.patientName || '',
        phone: appointment.patientPhone || '',
        email: appointment.patientEmail,
        internalId: (appointment as any).internalPatientId,
        spruceContactId: appointment.patientId,
      });
    } else if (initialPatient) {
      // Pre-populate with patient from triage board / AI scheduling suggestion
      setFormData({
        patientName: initialPatient.name || '',
        patientPhone: initialPatient.phone || '',
        patientEmail: initialPatient.email || '',
        patientDOB: '',
        patientGender: '',
        patientId: initialPatient.spruceContactId || '', // Spruce ID (if available)
        internalPatientId: initialPatient.internalId || '',
        appointmentType: '',
        serviceId: '',
        date: initialDraft?.date || format(new Date(), 'yyyy-MM-dd'),
        time: '09:00',
        duration: 30,
        provider: '',
        specialistId: '',
        room: '',
        presentingComplaints: initialDraft?.presentingComplaints || '',
        clinicalNotes: '',
      });
      setSelectedPatient({
        id: initialPatient.internalId || '',
        name: initialPatient.name || '',
        phone: initialPatient.phone || '',
        email: initialPatient.email,
        internalId: initialPatient.internalId,
        spruceContactId: initialPatient.spruceContactId,
      });
      setSearchQuery('');
      setPatientResults([]);
    } else {
      // Reset form for new appointment
      setFormData({
        patientName: '',
        patientPhone: '',
        patientEmail: '',
        patientDOB: '',
        patientGender: '',
        patientId: '',
        internalPatientId: '',
        appointmentType: '',
        serviceId: '',
        date: format(new Date(), 'yyyy-MM-dd'),
        time: '09:00',
        duration: 30,
        provider: '',
        specialistId: '',
        room: '',
        presentingComplaints: '',
        clinicalNotes: '',
      });
      setSelectedPatient(null);
      setSearchQuery('');
      setPatientResults([]);
    }
    durationTouched.current = false;
  }, [appointment, open, initialPatient, initialDraft, prefillFrom, services, specialists]);

  // What a new appointment opens on: the first service and the provider
  // paired with it (a lab draw is the nurse's, a consultation the doctor's),
  // with the length of that pairing. Declared after the reset above so its
  // update lands after the reset's; the length is re-derived further down
  // once both are known.
  useEffect(() => {
    if (!open || appointment) return;
    setFormData(prev => {
      const next = defaultNewAppointmentPairing({
        services,
        specialists,
        pairings: mappings,
        current: { serviceId: prev.serviceId, specialistId: prev.specialistId, appointmentType: prev.appointmentType },
      });
      if (!next) return prev;
      const service = services.find(s => s.id === next.serviceId);
      const specialist = specialists.find(s => s.id === next.specialistId);
      return {
        ...prev,
        serviceId: next.serviceId,
        appointmentType: service?.name || prev.appointmentType,
        specialistId: next.specialistId,
        provider: specialist?.name || prev.provider,
      };
    });
  }, [open, appointment, services, specialists, mappings, formData.serviceId, formData.specialistId, formData.appointmentType]);

  // A service has one duration for the whole practice; a provider may override it
  // for their own pairing (a consultation with Dr. Levitan runs longer than one
  // with another provider). Fall back to the service, then to 30 minutes.
  const resolveDuration = useCallback((serviceId: string, specialistId: string): number => {
    const override = mappings.find(
      m => m.serviceId === serviceId && m.specialistId === specialistId && m.duration
    );
    if (override?.duration) return override.duration;
    return services.find(s => s.id === serviceId)?.duration || 30;
  }, [mappings, services]);

  // Re-derive the duration for a NEW appointment once both the service and the
  // provider are known. The two default-selection effects above land in separate
  // passes, so neither can resolve the pair on its own.
  useEffect(() => {
    if (!open || appointment) return;
    if (durationTouched.current) return;
    if (!formData.serviceId || !formData.specialistId) return;
    const resolved = resolveDuration(formData.serviceId, formData.specialistId);
    setFormData(prev => (prev.duration === resolved ? prev : { ...prev, duration: resolved }));
  }, [open, appointment, formData.serviceId, formData.specialistId, resolveDuration]);

  // The type this appointment was recorded as, when it is not a configured
  // service — so the empty Select can explain itself instead of just being empty.
  const unmatchedType = useMemo(() => {
    if (formData.serviceId) return '';
    const recorded = String(formData.appointmentType || '').trim();
    if (!recorded) return '';
    // Matched loosely, because our own booking code writes this as a slug.
    return findServiceByName(services, recorded) ? '' : recorded;
  }, [formData.serviceId, formData.appointmentType, services]);

  const handleServiceChange = (serviceId: string) => {
    const service = services.find(s => s.id === serviceId);
    if (service) {
      durationTouched.current = false;
      setFormData(prev => {
        // Changing the service moves the provider to the one paired with it
        // (a lab draw is the nurse's, a consultation the doctor's), unless
        // the selected provider is paired with it too.
        // New appointments only: an edit keeps the provider it was booked with.
        const pairedId = appointment ? null : providerForService(service.id, mappings, prev.specialistId);
        const paired = pairedId ? specialists.find(s => s.id === pairedId) : null;
        const specialistId = paired ? paired.id : prev.specialistId;
        return {
          ...prev,
          serviceId: service.id,
          appointmentType: service.name,
          specialistId,
          provider: paired ? paired.name : prev.provider,
          duration: resolveDuration(service.id, specialistId),
        };
      });
    }
  };

  const handleProviderChange = (specialistId: string) => {
    const specialist = specialists.find(s => s.id === specialistId);
    if (specialist) {
      durationTouched.current = false;
      setFormData(prev => ({
        ...prev,
        specialistId: specialist.id,
        provider: specialist.name,
        duration: resolveDuration(prev.serviceId, specialist.id),
      }));
    }
  };

  const handlePatientSelect = (patient: Patient) => {
    setSelectedPatient(patient);
    setFormData(prev => ({
      ...prev,
      patientName: patient.name,
      patientPhone: patient.phone,
      patientEmail: patient.email || '',
      patientId: patient.spruceContactId || patient.id, // Spruce ID
      internalPatientId: patient.internalId || '', // Internal ID (e.g., LMD-00001)
    }));
    setSearchQuery('');
    setPatientResults([]);
  };

  const handleTimeAdjust = (minutes: number) => {
    const base = parseTypedTime(timeDraftRef.current) || timeValueRef.current;
    const [hours, mins] = base.split(':').map(Number);
    let totalMinutes = hours * 60 + mins + minutes;
    
    // Clamp between 5:00 and 21:00
    totalMinutes = Math.max(5 * 60, Math.min(21 * 60, totalMinutes));
    
    const newHours = Math.floor(totalMinutes / 60);
    const newMins = totalMinutes % 60;
    
    const next = `${newHours.toString().padStart(2, '0')}:${newMins.toString().padStart(2, '0')}`;
    const shown = formatClock(next);
    timeValueRef.current = next;
    timeDraftRef.current = shown;
    setTimeDraft(shown);
    setFormData(prev => ({
      ...prev,
      time: next,
    }));
  };

  const calculateEndTime = () => endTimeLabel(formData.time, formData.duration);

  const handleSave = async () => {
    setIsCreating(true);
    setError(null);
    
    try {
      // Format times as local (no timezone conversion) - just use the date and time strings directly
      const startTimeLocal = `${formData.date}T${formData.time}:00`;
      
      // Calculate end time
      const [hours, minutes] = formData.time.split(':').map(Number);
      const totalMinutes = hours * 60 + minutes + formData.duration;
      const endHours = Math.floor(totalMinutes / 60) % 24;
      const endMins = totalMinutes % 60;
      const endTimeStr = `${endHours.toString().padStart(2, '0')}:${endMins.toString().padStart(2, '0')}`;
      const endTimeLocal = `${formData.date}T${endTimeStr}:00`;

      const appointmentData = {
        patientName: formData.patientName,
        patientPhone: formData.patientPhone,
        patientEmail: formData.patientEmail,
        patientDOB: formData.patientDOB,
        patientGender: formData.patientGender,
        patientId: formData.patientId,
        startTime: startTimeLocal,
        endTime: endTimeLocal,
        appointmentType: formData.appointmentType.toLowerCase().replace(/\s+/g, '_'),
        serviceType: formData.appointmentType.toLowerCase().replace(/\s+/g, '_'),
        duration: formData.duration,
        provider: formData.provider,
        specialistId: formData.specialistId,
        room: formData.room,
        presentingComplaints: formData.presentingComplaints,
        clinicalNotes: formData.clinicalNotes,
        status: appointment?.status || 'scheduled',
        title: `${formData.appointmentType} - ${formData.patientName}`,
      };

      // Call API to create/update appointment
      let response;
      if (isEdit && appointment?.id) {
        // Update existing appointment
        response = await schedulingApi.updateAppointment(appointment.id.toString(), appointmentData);
      } else {
        // Create new appointment - need to format data for quickSchedule
        const createData = {
          ...appointmentData,
          preferredDate: formData.date,
          preferredTime: formData.time,
          serviceType: formData.appointmentType,
          // Provenance flag persisted server-side for the History timeline badge.
          aiAssisted: initialDraft?.aiAssisted === true,
        };
        response = await schedulingApi.createAppointment(createData);
      }
      
      // Extract the ID from the API response - API returns { success: true, data: { id: "..." } }
      const responseData = response.data?.data || response.data || {};
      const savedId = responseData.id || appointment?.id;
      
      if (!savedId) {
        throw new Error('Failed to get appointment ID from response');
      }
      
      const savedAppointment: Appointment = {
        id: savedId,
        ...appointmentData,
        ...responseData, // Include any additional fields from response
        createdAt: responseData.createdAt || appointment?.createdAt || new Date().toISOString(),
        updatedAt: responseData.updatedAt || new Date().toISOString(),
      };

      onSave(savedAppointment);
    } catch (err: any) {
      console.error('Failed to save appointment:', err);
      setError(err?.response?.data?.error || 'Failed to save appointment. Please try again.');
    } finally {
      setIsCreating(false);
    }
  };

  const formatTimeDisplay = (time: string) => formatClock(time);

  return (
    <Dialog 
      open={open} 
      onClose={onClose} 
      maxWidth="md" 
      fullWidth
      PaperComponent={draggable ? PaperComponent : undefined}
      aria-labelledby="draggable-dialog-title"
    >
      <DialogTitle 
        id="draggable-dialog-title"
        sx={{ cursor: draggable ? 'move' : 'default' }}
      >
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <CalendarIcon color="primary" />
            <Typography variant="h6">
              {isEdit ? 'Edit Appointment' : 'New Appointment'}
            </Typography>
          </Box>
          <IconButton onClick={onClose}>
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}
        
        {isLoading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress />
          </Box>
        ) : (
          <>
            {/* Search Contacts */}
            <Paper variant="outlined" sx={{ p: 2, mb: 3 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                <SearchIcon color="action" />
                <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                  Search Contacts
                </Typography>
              </Box>
              
              <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
                <Tabs
                  value={searchMode}
                  onChange={(_, value) => setSearchMode(value)}
                  sx={{ minHeight: 32 }}
                >
                  <Tab label="Search by Name" value="name" sx={{ minHeight: 32, py: 0 }} />
                  <Tab label="Search by Phone" value="phone" sx={{ minHeight: 32, py: 0 }} />
                </Tabs>
              </Box>
              
              <TextField
                fullWidth
                placeholder={searchMode === 'name' ? 'Enter patient name...' : 'Enter phone number...'}
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                size="small"
                InputProps={{
                  endAdornment: isSearching && <CircularProgress size={20} />,
                }}
              />
              
              {patientResults.length > 0 && (
                <Paper variant="outlined" sx={{ mt: 1, maxHeight: 200, overflow: 'auto' }}>
                  <List dense>
                    {patientResults.map((patient, idx) => (
                      <React.Fragment key={patient.id}>
                        <ListItemButton onClick={() => handlePatientSelect(patient)}>
                          <ListItemAvatar>
                            <Avatar sx={{ bgcolor: 'primary.main', width: 32, height: 32 }}>
                              <PersonIcon fontSize="small" />
                            </Avatar>
                          </ListItemAvatar>
                          <ListItemText
                            primary={patient.name}
                            secondary={patient.phone}
                          />
                          {patient.internalId && (
                            <Chip label={patient.internalId} size="small" color="primary" variant="outlined" />
                          )}
                        </ListItemButton>
                        {idx < patientResults.length - 1 && <Divider />}
                      </React.Fragment>
                    ))}
                  </List>
                </Paper>
              )}
              
              {selectedPatient && (
                <Box sx={{ mt: 1, p: 1, bgcolor: 'success.50', borderRadius: 1, display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                  <Chip 
                    label={`Selected: ${selectedPatient.name}`} 
                    color="success" 
                    onDelete={() => {
                      setSelectedPatient(null);
                      setFormData(prev => ({ ...prev, patientName: '', patientPhone: '', patientEmail: '', patientId: '', internalPatientId: '' }));
                    }}
                  />
                  {selectedPatient.internalId && (
                    <Chip label={`Internal: ${selectedPatient.internalId}`} size="small" color="primary" variant="outlined" />
                  )}
                  {selectedPatient.spruceContactId && (
                    <Chip label={`Spruce ID`} size="small" color="default" variant="outlined" />
                  )}
                </Box>
              )}
            </Paper>

            <Grid container spacing={3}>
              {/* Left Column - Appointment Details */}
              <Grid size={{ xs: 12, md: 6 }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 2 }}>
                  Appointment Details
                </Typography>

                <FormControl fullWidth size="small" sx={{ mb: unmatchedType ? 0.5 : 2 }}>
                  <InputLabel>Appointment Type *</InputLabel>
                  <Select
                    value={formData.serviceId}
                    label="Appointment Type *"
                    onChange={(e) => handleServiceChange(e.target.value)}
                  >
                    {services.map(service => (
                      <MenuItem key={service.id} value={service.id}>
                        {service.name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
                {/* Most appointments here came from the Practice Fusion import
                    and carry a type the practice never configured as a service
                    ("Follow-Up Visit", "Wellness Exam"). The Select can only
                    hold real services, so it renders empty and the required
                    field looks broken. Say what the appointment actually is,
                    rather than leaving a blank and no explanation. */}
                {unmatchedType && (
                  <Typography variant="caption" color="warning.main" sx={{ display: 'block', mb: 2 }}>
                    Recorded as <strong>{unmatchedType}</strong>, which is not one of the practice's
                    services. Choose the service it belongs to.
                  </Typography>
                )}

                <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
                  <TextField
                    label="DATE *"
                    type="date"
                    value={formData.date}
                    onChange={(e) => setFormData(prev => ({ ...prev, date: e.target.value }))}
                    size="small"
                    fullWidth
                    InputLabelProps={{ shrink: true }}
                  />
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <Typography variant="caption" sx={{ minWidth: 40 }}>TIME *</Typography>
                    <IconButton size="small" onClick={() => handleTimeAdjust(-15)}>
                      <RemoveIcon fontSize="small" />
                    </IconButton>
                    <TextField
                      value={timeDraft}
                      onFocus={() => { timeFieldFocused.current = true; }}
                      onChange={(e) => {
                        const next = e.target.value.toUpperCase();
                        timeDraftRef.current = next;
                        setTimeDraft(next);
                        const parsed = parseTypedTime(next);
                        if (parsed) {
                          timeValueRef.current = parsed;
                          setFormData(prev => ({ ...prev, time: parsed }));
                        }
                      }}
                      onBlur={() => {
                        timeFieldFocused.current = false;
                        const parsed = parseTypedTime(timeDraftRef.current);
                        if (parsed) {
                          timeValueRef.current = parsed;
                          const shown = formatClock(parsed);
                          timeDraftRef.current = shown;
                          setTimeDraft(shown);
                          setFormData(prev => ({ ...prev, time: parsed }));
                        } else {
                          const shown = formatClock(timeValueRef.current);
                          timeDraftRef.current = shown;
                          setTimeDraft(shown);
                        }
                      }}
                      size="small"
                      placeholder="9:30 AM"
                      inputProps={{ 'aria-label': 'Appointment time', style: { textAlign: 'center', fontWeight: 600 } }}
                      sx={{ width: 118 }}
                    />
                    <IconButton size="small" onClick={() => handleTimeAdjust(15)}>
                      <AddIcon fontSize="small" />
                    </IconButton>
                  </Box>
                </Box>

                <Box sx={{ mb: 2 }}>
                  <Typography variant="caption" color="text.secondary">
                    END TIME: {calculateEndTime()}
                  </Typography>
                </Box>

                <FormControl fullWidth size="small" sx={{ mb: 2 }}>
                  <InputLabel>Duration (Minutes) *</InputLabel>
                  <Select
                    value={formData.duration}
                    label="Duration (Minutes) *"
                    onChange={(e) => {
                      durationTouched.current = true;
                      setFormData(prev => ({ ...prev, duration: Number(e.target.value) }));
                    }}
                  >
                    {durationChoices(formData.duration).map(dur => (
                      <MenuItem key={dur} value={dur}>{dur} minutes</MenuItem>
                    ))}
                  </Select>
                </FormControl>

                <FormControl fullWidth size="small" sx={{ mb: 2 }}>
                  <InputLabel>Provider</InputLabel>
                  <Select
                    value={formData.specialistId}
                    label="Provider"
                    onChange={(e) => handleProviderChange(e.target.value)}
                  >
                    {specialists.map(specialist => (
                      <MenuItem key={specialist.id} value={specialist.id}>
                        {specialist.name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>

                <TextField
                  label="Room"
                  value={formData.room}
                  onChange={(e) => setFormData(prev => ({ ...prev, room: e.target.value }))}
                  size="small"
                  fullWidth
                  placeholder="e.g., Room 101"
                  sx={{ mb: 2 }}
                />
              </Grid>

              {/* Right Column - Patient Information */}
              <Grid size={{ xs: 12, md: 6 }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 2 }}>
                  Patient Information
                </Typography>

                <TextField
                  label="Patient Name *"
                  value={formData.patientName}
                  onChange={(e) => setFormData(prev => ({ ...prev, patientName: e.target.value }))}
                  size="small"
                  fullWidth
                  sx={{ mb: 2 }}
                  required
                />

                <TextField
                  label="Phone Number *"
                  value={formData.patientPhone}
                  onChange={(e) => setFormData(prev => ({ ...prev, patientPhone: e.target.value }))}
                  size="small"
                  fullWidth
                  sx={{ mb: 2 }}
                  placeholder="(305) 555-1234"
                />

                <TextField
                  label="Email Address"
                  value={formData.patientEmail}
                  onChange={(e) => setFormData(prev => ({ ...prev, patientEmail: e.target.value }))}
                  size="small"
                  fullWidth
                  sx={{ mb: 2 }}
                />

                <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
                  <TextField
                    label="Date of Birth"
                    type="date"
                    value={formData.patientDOB}
                    onChange={(e) => setFormData(prev => ({ ...prev, patientDOB: e.target.value }))}
                    size="small"
                    fullWidth
                    InputLabelProps={{ shrink: true }}
                  />
                  <FormControl fullWidth size="small">
                    <InputLabel>Gender</InputLabel>
                    <Select
                      value={formData.patientGender}
                      label="Gender"
                      onChange={(e) => setFormData(prev => ({ ...prev, patientGender: e.target.value }))}
                    >
                      <MenuItem value="Male">Male</MenuItem>
                      <MenuItem value="Female">Female</MenuItem>
                      <MenuItem value="Other">Other</MenuItem>
                    </Select>
                  </FormControl>
                </Box>
                
                {/* Show Patient IDs - Spruce ID and Internal ID */}
                {(formData.patientId || formData.internalPatientId) && (
                  <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
                    {formData.patientId && (
                      <TextField
                        label="Patient ID (Spruce)"
                        value={formData.patientId}
                        size="small"
                        fullWidth
                        disabled
                        sx={{ bgcolor: 'grey.50' }}
                      />
                    )}
                    {formData.internalPatientId && (
                      <TextField
                        label="Internal ID"
                        value={formData.internalPatientId}
                        size="small"
                        fullWidth
                        disabled
                        sx={{ bgcolor: 'primary.50' }}
                      />
                    )}
                  </Box>
                )}
              </Grid>

              {/* Clinical Information */}
              <Grid size={{ xs: 12 }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 2 }}>
                  Clinical Information
                </Typography>

                <TextField
                  label="Presenting Complaints"
                  value={formData.presentingComplaints}
                  onChange={(e) => setFormData(prev => ({ ...prev, presentingComplaints: e.target.value }))}
                  size="small"
                  fullWidth
                  multiline
                  rows={2}
                  placeholder="Describe the patient's symptoms, concerns, or reason for visit..."
                  sx={{ mb: 2 }}
                />

                <TextField
                  label="Clinical Notes"
                  value={formData.clinicalNotes}
                  onChange={(e) => setFormData(prev => ({ ...prev, clinicalNotes: e.target.value }))}
                  size="small"
                  fullWidth
                  multiline
                  rows={2}
                  placeholder="Additional clinical notes..."
                />
              </Grid>
            </Grid>
          </>
        )}
      </DialogContent>

      {/* Cancelling the appointment sits apart from the buttons that close the
          dialog. The dismissing button used to be called "Cancel", which is
          the word a staff member looking to cancel the appointment reads —
          they pressed it, nothing was cancelled, and the slot stayed blocked.
          It is "Close" now, so "Cancel appointment" is the only Cancel here. */}
      <DialogActions sx={{ p: 2, gap: 1, flexWrap: 'wrap' }}>
        {canCancelAppointment(appointment) && onCancelAppointment && (
          <Button
            onClick={() => appointment && onCancelAppointment(appointment)}
            color="error"
            variant="outlined"
            startIcon={<CancelIcon />}
            disabled={isCreating}
            sx={{ mr: 'auto' }}
          >
            Cancel appointment
          </Button>
        )}
        <Button onClick={onClose} variant="outlined" disabled={isCreating}>
          Close
        </Button>
        <Button 
          onClick={handleSave} 
          variant="contained"
          disabled={!formData.patientName || !formData.patientPhone || isCreating}
          startIcon={isCreating && <CircularProgress size={16} color="inherit" />}
        >
          {isCreating ? 'Creating...' : (isEdit ? 'Save Changes' : 'Create Appointment')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default AppointmentModal;
